import React from "react";
import { format, getDay, getYear, isValid } from "date-fns";
import StatWrapper from "../shared/StatWrapper";
import Heading from "hardcover-ui/components/Heading";
import Text from "hardcover-ui/components/Text";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "hardcover-ui/components/Tooltip";
import { useChartTheme } from "../contexts/ChartThemeContext";
import { usePage } from "@inertiajs/react";
import { UserStatsPageProps } from "pages/clientverse/users/stats/index.props";

interface ReadingGraphStatProps {
  size?: "sm" | "md" | "lg" | "xl";
}

const ReadingGraphStat: React.FC<ReadingGraphStatProps> = ({ size = "xl" }) => {
  const { stats } = usePage<UserStatsPageProps>().props;
  const { readingActivity: data } = stats;
  const { theme } = useChartTheme();

  if (!data || !data.days || data.days.length === 0) {
    return (
      <StatWrapper title="Reading Activity" size={size}>
        <div className="flex flex-col w-full h-full">
          <Heading level={3} size="sm" className="mb-4">
            No reading activity data available
          </Heading>
        </div>
      </StatWrapper>
    );
  }

  const year = getYear(stats.from || new Date());
  const fromDate = stats.from || new Date();
  const toDate = stats.to || new Date();

  const getColorScale = () => {
    const baseColor =
      theme === "colorful" ? "var(--chart-colorful-2)" : "var(--chart-mono-1)";
    return [
      `color-mix(in srgb, ${baseColor}, transparent 65%)`,
      `color-mix(in srgb, ${baseColor}, transparent 40%)`,
      `color-mix(in srgb, ${baseColor}, transparent 20%)`,
      `color-mix(in srgb, ${baseColor}, transparent 5%)`,
      baseColor,
    ];
  };

  const colorScale = getColorScale();

  const getColor = (value: number) => {
    if (!value) return colorScale[0];
    const index = Math.floor((value / 100) * (colorScale.length - 1));
    return colorScale[index];
  };

  const cellSize = 16;
  const cellGap = 6;
  const weekCount = 53; // Always show 53 weeks (GitHub style)

  const getPeriodLabel = () => {
    if (!stats.selectedPeriod) return format(new Date(), "yyyy");

    switch (stats.selectedPeriod) {
      case "life":
        return format(new Date(), "yyyy");
      case "year":
        return format(fromDate, "yyyy");
      case "month":
        return format(fromDate, "MMMM yyyy");
      case "custom":
        return `${format(fromDate, "MMM d, yyyy")} - ${format(
          toDate,
          "MMM d, yyyy"
        )}`;
      default:
        return format(new Date(), "yyyy");
    }
  };

  return (
    <StatWrapper title={`Reading Activity (${year})`} size={size}>
      <div className="flex flex-col w-full h-full">
        <Heading level={3} size="sm" className="mb-4">
          Your reading activity for {getPeriodLabel()}
        </Heading>
        <div
          className="flex-shrink-0 relative overflow-x-auto mb-8"
          style={{ height: `${8 * (cellSize + cellGap)}px` }}
        >
          <svg
            width={`${weekCount * (cellSize + cellGap)}px`}
            height="100%"
            viewBox={`0 0 ${weekCount * (cellSize + cellGap)} ${
              8 * (cellSize + cellGap)
            }`}
            className="lg:w-full"
          >
            <g transform={`translate(0, ${cellSize + cellGap})`}>
              {data.days.map((day) => {
                if (!day || !day.date) return null;

                // Ensure date is a valid Date object
                const date = new Date(day.date);
                if (!isValid(date)) return null;

                const weekIndex = Math.floor(
                  (getDay(date) + data.days.indexOf(day)) / 7
                );
                const dayIndex = getDay(date);
                const totalValue = day.pages || 0;

                return (
                  <Tooltip key={date.toISOString()}>
                    <TooltipTrigger asChild>
                      <rect
                        x={weekIndex * (cellSize + cellGap)}
                        y={dayIndex * (cellSize + cellGap)}
                        width={cellSize}
                        height={cellSize}
                        rx={2}
                        fill={getColor(totalValue)}
                        className="transition-all hover:opacity-80"
                      />
                    </TooltipTrigger>
                    <TooltipContent>
                      <div className="flex flex-col">
                        <Text className="font-bold">
                          {format(date, "MMMM d, yyyy")}
                        </Text>
                        <Text variant="secondary">{totalValue} pages read</Text>
                      </div>
                    </TooltipContent>
                  </Tooltip>
                );
              })}
            </g>
          </svg>
        </div>
        <div className="flex justify-between items-center">
          <Text variant="secondary" size="sm">
            Less
          </Text>
          <div className="flex gap-1">
            {colorScale.map((color, index) => (
              <div
                key={index}
                className="w-4 h-4 rounded-sm"
                style={{ backgroundColor: color }}
              />
            ))}
          </div>
          <Text variant="secondary" size="sm">
            More
          </Text>
        </div>
      </div>
    </StatWrapper>
  );
};

export default ReadingGraphStat;
